import { useQueryClient } from "@tanstack/solid-query";
import {
  type Component,
  createEffect,
  createMemo,
  For,
  Match,
  on,
  Show,
  Switch,
  untrack,
} from "solid-js";
import { useI18n } from "@/api/i18n";
import { TALENT_POOL_KEY } from "@/api/query-keys";
import { Button } from "@/components/ui/button";
import { Loading } from "@/components/ui/loading";
import type { FreelancerSearchFilters } from "@/domains/company/active-search/types";
import { useCompanyStore } from "@/domains/company/company/store/company-store";
import { usePostHog } from "@/hooks/usePostHog";
import { useInfiniteTalentPoolMatch } from "../../api";
import type { PoolMatchFreelancer } from "../../api/types";
import { useTalentPool } from "../../context/talent-pool-provider";
import { mapTalentPoolMatchError } from "../../utils/match-errors";
import { matchHasActiveFilters } from "../../utils/match-filters";
import { resolveMatchTabState } from "../../utils/match-tab-state";
import { MatchTabGate } from "./match-tab-gate";
import { MatchEmptyFiltered, MatchEmptyPool } from "./match-tab-states";
import { PoolMatchCard } from "./pool-match-card";

export type PoolMatchPanelProps = {
  jobOfferId: string;
  /** The shared URL filters (same accessor the Ricerca tab reads). */
  filters: () => FreelancerSearchFilters;
  /** Open the profile sheet — `disableContact` set for non-discoverable rows. */
  onViewProfile: (
    item: PoolMatchFreelancer,
    opts: { disableContact: boolean },
  ) => void;
  /** Navigate to the search/offers surface (invalid-offer recovery). */
  onBackToSearch: () => void;
  /** Clear the shared filters (empty_filtered recovery). */
  onResetFilters: () => void;
  /** Switch to the Ricerca tab (empty_pool invite-to-save). */
  onGoToSearchTab: () => void;
};

/**
 * The pool tab's data panel — mounted whenever the pool tab is active. The match
 * query self-gates on `capabilities.enabled` (plus company/offer presence), so it
 * fires NO request under a locked plan (AC8) or on the Ricerca tab (AC7 lazy).
 * `resolveMatchTabState` drives the content-scoped `MatchTabGate`; the list,
 * count (panel header, AC7), empty states (AC9), and load-more-with-inline-retry
 * (finding #5) live in the gate's content slot.
 */
export const PoolMatchPanel: Component<PoolMatchPanelProps> = (props) => {
  const { t } = useI18n();
  const tp = useTalentPool();
  const { capture } = usePostHog();
  const queryClient = useQueryClient();
  const [companyState] = useCompanyStore();
  const companyId = () => companyState.selectedCompany?.id;

  const enabled = createMemo(
    () => !!companyId() && !!props.jobOfferId && tp.capabilities.enabled,
  );

  const matchQuery = useInfiniteTalentPoolMatch(() => ({
    companyId: companyId(),
    jobOfferId: props.jobOfferId,
    filters: props.filters(),
    enabled: enabled(),
  }));

  const items = createMemo(
    () => matchQuery.data?.pages.flatMap((page) => page.data) ?? [],
  );
  const total = createMemo(
    () => matchQuery.data?.pages?.[0]?.pagination?.total ?? 0,
  );
  const hasActiveFilters = createMemo(() =>
    matchHasActiveFilters(props.filters()),
  );

  // Seed membership from each landed page (OQ1) — no `/membership` round-trip for
  // pool rows. `seedFromMatch` is dedup-gated on `hydratedIds`, so re-seeding an
  // already-seeded page is a cheap no-op AND the un-heart tombstone survives
  // (finding #1): iterating every page each run is safe and idempotent.
  createEffect(() => {
    const pages = matchQuery.data?.pages;
    if (!pages) return;
    for (const page of pages) tp.seedFromMatch(page.data);
  });

  const queryStatus = createMemo(() =>
    matchQuery.isFetchNextPageError ? "success" : matchQuery.status,
  );
  const errorKind = createMemo(() =>
    matchQuery.error ? mapTalentPoolMatchError(matchQuery.error) : undefined,
  );
  createEffect(
    on(
      () => matchQuery.error,
      (error) => {
        if (!error) return;
        untrack(() =>
          capture("fail_semantic_search", {
            endpoint: "talent_pool_match",
            error_kind: mapTalentPoolMatchError(error).kind,
          }),
        );
      },
    ),
  );

  const state = createMemo(() =>
    resolveMatchTabState({
      capabilitiesStatus: tp.capabilities.status,
      enabled: tp.capabilities.enabled,
      queryStatus: queryStatus(),
      errorKind: errorKind(),
      itemCount: items().length,
      hasActiveFilters: hasActiveFilters(),
    }),
  );

  const handleRetry = () => {
    if (tp.capabilities.status === "error") {
      void queryClient.invalidateQueries({
        queryKey: [TALENT_POOL_KEY, "capabilities"],
      });
      return;
    }
    void matchQuery.refetch();
  };

  return (
    <MatchTabGate
      state={state()}
      plan={tp.capabilities.plan ?? ""}
      onRetry={handleRetry}
      onBackToSearch={props.onBackToSearch}
    >
      <Switch>
        <Match when={state().state === "empty_pool"}>
          <MatchEmptyPool onGoToSearchTab={props.onGoToSearchTab} />
        </Match>
        <Match when={state().state === "empty_filtered"}>
          <MatchEmptyFiltered onResetFilters={props.onResetFilters} />
        </Match>
        <Match when={state().state === "list"}>
          <div class="space-y-4">
            {/* Per-tab count lives HERE, in the panel header (AC7, lazy-safe). */}
            <div class="space-y-1">
              <p class="text-gray-600">
                {t("company.talent_pool.match.count", { count: total() })}
              </p>
              <p class="text-xs text-muted-foreground">
                {t("company.talent_pool.match.sorted_by_affinity")}
              </p>
            </div>

            <For each={items()}>
              {(item) => (
                <PoolMatchCard
                  item={item}
                  onViewProfile={props.onViewProfile}
                />
              )}
            </For>

            <Show when={matchQuery.hasNextPage}>
              <div class="flex flex-col items-center justify-center gap-2 h-24 border-t border-gray-300 mt-4 mb-4">
                {/* Load-more failure keeps the loaded pages + inline retry
                    (finding #5) — never wipes the list. */}
                <Show when={matchQuery.isFetchNextPageError}>
                  <p class="text-sm text-destructive">
                    {t("company.talent_pool.match.load_more_error")}
                  </p>
                </Show>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => matchQuery.fetchNextPage()}
                  disabled={matchQuery.isFetchingNextPage}
                >
                  {matchQuery.isFetchNextPageError
                    ? t("company.talent_pool.list.retry")
                    : t("company.talent_pool.match.load_more")}
                </Button>
              </div>
            </Show>

            <Show when={matchQuery.isFetchingNextPage}>
              <Loading />
            </Show>
          </div>
        </Match>
      </Switch>
    </MatchTabGate>
  );
};
